/**
 * GlobalView — Earthquake Layer Renderer
 *
 * Renders earthquakes as concentric shockwave rings that smoothly
 * animate outward from each epicenter.
 *
 * Supports TWO sub-layers:
 *   - Global (USGS) earthquakes
 *   - Swiss (SED/FDSN) earthquakes — rendered smaller
 *
 * Architecture:
 *   - Wall geometry created ONCE (static positions + heights)
 *   - Smooth animation via CallbackProperty on material color alpha
 *   - Gaussian wave pulses sweep center → outer, continuously
 *   - Pre-allocated Color objects per ring (zero GC pressure)
 *   - Wall height capped at 50 km (proper globe occlusion)
 *   - requestAnimationFrame drives continuous rendering
 *
 * Data: USGS GeoJSON (global) + SED FDSN text (Swiss)
 */

import * as Cesium from 'cesium';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore, selectEarthquakeCount } from '../../stores/entityStore';
import {
  fetchEarthquakes,
  magnitudeToColor,
  type USGSFeed,
} from '../usgsEarthquakeClient';
import { fetchSwissEarthquakes } from '../sedEarthquakeClient';
import type { Entity } from '@globalview/shared';

// ─── Config ──────────────────────────────────────────────────────

/** Max earthquakes to render per sub-layer */
const MAX_QUAKES_GLOBAL = 60;
const MAX_QUAKES_SWISS = 80;

/** Number of concentric ring positions per earthquake */
const RING_COUNT = 8;

/** Number of gradient bands per ring */
const BANDS_PER_RING = 2;

/** Gradient decay steepness (vertical fade within each ring) */
const GRADIENT_DECAY = 3.5;

/** Peak alpha for the brightest wave moment */
const PEAK_ALPHA = 0.55;

/** Minimum ambient alpha (rings faintly visible even without wave) */
const AMBIENT_ALPHA = 0.10;

/** Ground polygon alpha */
const GROUND_ALPHA = 0.20;

/** Polygon segments for circular walls */
const CIRCLE_SEGMENTS = 16;

/** Wave animation cycle (ms) — one full outward sweep */
const CYCLE_MS = 5000;

/** Number of simultaneous wave fronts */
const NUM_WAVES = 2;

/** Gaussian width of each wave pulse (in ring-fraction space 0..1) */
const WAVE_SIGMA = 0.10;

/** Data refresh interval (ms) */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/** Max wall height (meters) — keeps walls below horizon */
const MAX_HEIGHT = 50_000;

/** Max ring spread radius (meters) */
const MAX_RADIUS = 250_000;

/** Swiss earthquakes scale factor (smaller than global) */
const SWISS_SCALE = 0.4;

/** Ring count for Swiss earthquakes (fewer rings = lighter) */
const SWISS_RING_COUNT = 5;

// ─── Ring Layout ─────────────────────────────────────────────────

interface RingSpec {
  radiusFrac: number;
  heightFrac: number;
}

function buildRingSpecs(ringCount: number): RingSpec[] {
  const specs: RingSpec[] = [];
  for (let i = 0; i < ringCount; i++) {
    const t = i / (ringCount - 1);
    const radiusFrac = 0.05 + 0.95 * Math.pow(t, 0.7);
    const heightFrac = Math.exp(-3.0 * t);
    specs.push({ radiusFrac, heightFrac });
  }
  return specs;
}

const RING_SPECS = buildRingSpecs(RING_COUNT);
const SWISS_RING_SPECS = buildRingSpecs(SWISS_RING_COUNT);

interface GradientBand {
  fracStart: number;
  fracEnd: number;
  alphaMult: number;
}

const GRADIENT_BANDS: GradientBand[] = (() => {
  const bands: GradientBand[] = [];
  for (let i = 0; i < BANDS_PER_RING; i++) {
    const fracStart = i / BANDS_PER_RING;
    const fracEnd = (i + 1) / BANDS_PER_RING;
    const mid = (fracStart + fracEnd) / 2;
    bands.push({ fracStart, fracEnd, alphaMult: Math.exp(-GRADIENT_DECAY * mid) });
  }
  return bands;
})();

// ─── Wave Math ───────────────────────────────────────────────────

/**
 * Gaussian pulse — returns 0..1 with peak at x == center.
 */
function gaussPulse(x: number, center: number): number {
  const d = x - center;
  return Math.exp(-(d * d) / (2 * WAVE_SIGMA * WAVE_SIGMA));
}

/**
 * Compute wave intensity for a ring at ringFrac (0..1) and time t (0..1).
 * Multiple wave fronts sweep from 0→1. Handles wrap-around.
 */
function waveIntensity(ringFrac: number, t: number): number {
  let best = 0;
  for (let w = 0; w < NUM_WAVES; w++) {
    const front = (t + w / NUM_WAVES) % 1.0;
    const pulse = Math.max(
      gaussPulse(ringFrac, front),
      gaussPulse(ringFrac, front - 1.0),
      gaussPulse(ringFrac, front + 1.0),
    );
    if (pulse > best) best = pulse;
  }
  return best;
}

// ─── Magnitude Helpers ──────────────────────────────────────────

function magnitudeToHeight(mag: number, scale = 1.0): number {
  if (mag < 0) mag = 0;
  return Math.min(5_000 * Math.pow(1.7, mag) * scale, MAX_HEIGHT * scale);
}

function magnitudeToRadius(mag: number, scale = 1.0): number {
  if (mag < 0) mag = 0;
  return Math.min(10_000 * Math.pow(1.6, mag) * scale, MAX_RADIUS * scale);
}

// ─── Circle Geometry ─────────────────────────────────────────────

function generateCirclePositions(
  centerLon: number,
  centerLat: number,
  radiusMeters: number,
): Cesium.Cartesian3[] {
  const positions: Cesium.Cartesian3[] = [];
  const centerCart = Cesium.Cartographic.fromDegrees(centerLon, centerLat);

  for (let i = 0; i <= CIRCLE_SEGMENTS; i++) {
    const angle = (i / CIRCLE_SEGMENTS) * 2 * Math.PI;
    const dLat = (radiusMeters * Math.cos(angle)) / 6_371_000;
    const dLon = (radiusMeters * Math.sin(angle)) /
      (6_371_000 * Math.cos(centerCart.latitude));

    positions.push(Cesium.Cartesian3.fromDegrees(
      Cesium.Math.toDegrees(centerCart.longitude + dLon),
      Cesium.Math.toDegrees(centerCart.latitude + dLat),
    ));
  }
  return positions;
}

function hashToPhase(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 1000) / 1000;
}

// ─── Sub-Layer Data ──────────────────────────────────────────────

interface SubLayerData {
  wallDataSource: Cesium.CustomDataSource;
  staticDataSource: Cesium.CustomDataSource;
  loaded: boolean;
  loading: boolean;
}

// ─── Module State ────────────────────────────────────────────────

interface QuakeLayerState {
  viewer: Cesium.Viewer;
  global: SubLayerData;
  swiss: SubLayerData;
  unsubscribeVisibility: (() => void) | null;
  refreshTimer: ReturnType<typeof setInterval> | null;
  currentFeed: USGSFeed;
}

let state: QuakeLayerState | null = null;
let animFrameId: number | null = null;
let animRunning = false;

// ─── Entity Creation ─────────────────────────────────────────────

/**
 * Build all Cesium entities for a sub-layer.
 * Wall geometry is static. Animation via CallbackProperty on material color alpha.
 */
function buildEntities(
  earthquakes: Entity[],
  wallDataSource: Cesium.CustomDataSource,
  staticDataSource: Cesium.CustomDataSource,
  maxQuakes: number,
  scale: number,
  ringSpecs: RingSpec[],
  skipWalls = false,
  terrainHeights?: Map<string, number>,
): void {
  wallDataSource.entities.removeAll();
  staticDataSource.entities.removeAll();

  const ringCount = ringSpecs.length;

  const sorted = [...earthquakes]
    .filter(e => ((e.properties as Record<string, unknown>).magnitude as number ?? 0) >= 0.5)
    .sort((a, b) => {
      const ma = (a.properties as Record<string, unknown>).magnitude as number ?? 0;
      const mb = (b.properties as Record<string, unknown>).magnitude as number ?? 0;
      return mb - ma;
    })
    .slice(0, maxQuakes);

  for (const eq of sorted) {
    const props = eq.properties as Record<string, unknown>;
    const mag = (props.magnitude as number) ?? 0;
    const place = (props.place as string) ?? 'Unknown';

    const lon = eq.position.lon;
    const lat = eq.position.lat;
    const maxHeight = magnitudeToHeight(mag, scale);
    const maxRadius = magnitudeToRadius(mag, scale);
    const baseColor = Cesium.Color.fromCssColorString(magnitudeToColor(mag));
    const phaseOffset = hashToPhase(eq.id);

    // ── Concentric ring walls with animated alpha ─────────────────
    // Skip walls for sub-layers on elevated terrain (e.g. Swiss Alps)
    if (!skipWalls) {
    for (let r = 0; r < ringCount; r++) {
      const spec = ringSpecs[r];
      const radius = maxRadius * spec.radiusFrac;
      const ringHeight = maxHeight * spec.heightFrac;

      if (radius < 200 || ringHeight < 100) continue;

      const positions = generateCirclePositions(lon, lat, radius);
      const ringFrac = r / (ringCount - 1); // 0..1

      for (let b = 0; b < GRADIENT_BANDS.length; b++) {
        const band = GRADIENT_BANDS[b];

        const minH = new Array(positions.length).fill(ringHeight * band.fracStart);
        const maxH = new Array(positions.length).fill(ringHeight * band.fracEnd);

        // Pre-allocate a scratch color for this ring+band combo
        const scratchColor = baseColor.clone();

        wallDataSource.entities.add({
          id: `eq-${eq.id}-r${r}-b${b}`,
          wall: {
            positions: new Cesium.ConstantProperty(positions),
            minimumHeights: new Cesium.ConstantProperty(minH),
            maximumHeights: new Cesium.ConstantProperty(maxH),
            material: new Cesium.ColorMaterialProperty(
              new Cesium.CallbackProperty(() => {
                const t = ((Date.now() / CYCLE_MS) + phaseOffset) % 1.0;
                const wave = waveIntensity(ringFrac, t);
                // Smooth alpha: ambient glow + wave peak, modulated by vertical band
                const alpha = (AMBIENT_ALPHA + (PEAK_ALPHA - AMBIENT_ALPHA) * wave) * band.alphaMult;
                scratchColor.alpha = alpha;
                return scratchColor;
              }, false),
            ),
            outline: false,
          },
          // Properties on innermost ring, first band only
          ...(r === 0 && b === 0 ? {
            properties: new Cesium.PropertyBag({
              entityId: eq.id,
              magnitude: mag,
              place,
              depth: props.depth,
              time: eq.timestamp,
              tsunami: props.tsunami,
              alert: props.alert,
              significance: props.significance,
              url: props.url,
              felt: props.felt,
              mmi: props.mmi,
            }),
          } : {}),
        });
      }
    }
    } // end if (!skipWalls)

    // ── Ground glow (static) ──────────────────────────────────────
    const groundRadius = skipWalls ? maxRadius * 0.25 : maxRadius * 0.4;
    const groundPos = generateCirclePositions(lon, lat, groundRadius);
    staticDataSource.entities.add({
      id: `eq-gnd-${eq.id}`,
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(groundPos),
        material: new Cesium.ColorMaterialProperty(
          baseColor.withAlpha(skipWalls ? GROUND_ALPHA * 1.5 : GROUND_ALPHA),
        ),
        outline: false,
        height: new Cesium.ConstantProperty(0),
        classificationType: new Cesium.ConstantProperty(
          skipWalls
            ? Cesium.ClassificationType.CESIUM_3D_TILE
            : Cesium.ClassificationType.BOTH,
        ),
      },
    });

    // ── Center point marker (static) ──────────────────────────────
    const pointSize = scale < 1 ? Math.max(3, mag * 1.8) : Math.max(4, mag * 2.5);
    const labelThreshold = scale < 1 ? 5.0 : 4.0;

    // For Swiss sub-layer: use sampled terrain height for absolute positioning
    // so markers sit at the correct elevation, not at the WGS84 ellipsoid
    const terrainH = terrainHeights?.get(eq.id) ?? 0;
    const ptAlt = skipWalls ? terrainH + 50 : 0;
    const ptHeightRef = skipWalls
      ? Cesium.HeightReference.NONE    // absolute — we already computed the height
      : Cesium.HeightReference.CLAMP_TO_GROUND;

    staticDataSource.entities.add({
      id: `eq-pt-${eq.id}`,
      position: Cesium.Cartesian3.fromDegrees(lon, lat, ptAlt),
      point: {
        pixelSize: pointSize,
        color: baseColor.withAlpha(0.9),
        outlineColor: Cesium.Color.WHITE.withAlpha(0.6),
        outlineWidth: 1.5,
        heightReference: ptHeightRef,
        scaleByDistance: new Cesium.NearFarScalar(1e4, 1.5, 5e6, 0.3),
        disableDepthTestDistance: skipWalls ? Number.POSITIVE_INFINITY : undefined,
      },
      label: mag >= labelThreshold ? {
        text: `M${mag.toFixed(1)}`,
        font: '13px Inter, Helvetica, sans-serif',
        fillColor: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        heightReference: ptHeightRef,
        pixelOffset: new Cesium.Cartesian2(0, -16),
        scaleByDistance: new Cesium.NearFarScalar(1e4, 1.2, 2e6, 0.0),
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 2_000_000),
        disableDepthTestDistance: skipWalls ? Number.POSITIVE_INFINITY : undefined,
      } : undefined,
    });
  }

  console.log(
    `[QuakeLayer] Built ${sorted.length} earthquakes ` +
    `(${wallDataSource.entities.values.length} wall + ` +
    `${staticDataSource.entities.values.length} static entities) [${wallDataSource.name}]`,
  );
}

// ─── Animation (requestAnimationFrame) ──────────────────────────

/**
 * Drive continuous rendering so CallbackProperty alpha is evaluated.
 */
function animLoop(): void {
  if (!state || !animRunning) return;
  state.viewer.scene.requestRender();
  animFrameId = requestAnimationFrame(animLoop);
}

function startAnimation(): void {
  if (animRunning) return;
  animRunning = true;
  animFrameId = requestAnimationFrame(animLoop);
  console.log('[QuakeLayer] Animation started');
}

function stopAnimation(): void {
  animRunning = false;
  if (animFrameId != null) {
    cancelAnimationFrame(animFrameId);
    animFrameId = null;
  }
}

// ─── Data Loading ────────────────────────────────────────────────

async function loadGlobalData(): Promise<void> {
  if (!state || state.global.loading) return;

  state.global.loading = true;
  console.log('[QuakeLayer] Loading USGS global data...');

  try {
    const entities = await fetchEarthquakes(state.currentFeed);

    if (entities.length === 0) {
      console.warn('[QuakeLayer] No global earthquake data received');
      return;
    }

    // Clear only USGS earthquakes from entity store
    const store = useEntityStore.getState();
    const currentEntities = store.entities;
    const usgsIds: string[] = [];
    for (const [id, entity] of currentEntities) {
      if (entity.type === 'earthquake' && entity.source === 'usgs') usgsIds.push(id);
    }
    if (usgsIds.length > 0) store.removeEntities(usgsIds);
    store.upsertEntities(entities);

    buildEntities(entities, state.global.wallDataSource, state.global.staticDataSource, MAX_QUAKES_GLOBAL, 1.0, RING_SPECS, false);
    startAnimation();

    state.global.loaded = true;
    console.log(`[QuakeLayer] Loaded ${entities.length} global earthquakes`);
  } catch (err) {
    console.error('[QuakeLayer] Failed to load global data:', err);
  } finally {
    if (state) state.global.loading = false;
  }
}

async function loadSwissData(): Promise<void> {
  if (!state || state.swiss.loading) return;

  state.swiss.loading = true;
  console.log('[QuakeLayer] Loading SED Swiss data...');

  try {
    const entities = await fetchSwissEarthquakes();

    if (entities.length === 0) {
      console.warn('[QuakeLayer] No Swiss earthquake data received');
      return;
    }

    // Clear only SED earthquakes from entity store
    const store = useEntityStore.getState();
    const currentEntities = store.entities;
    const sedIds: string[] = [];
    for (const [id, entity] of currentEntities) {
      if (entity.type === 'earthquake' && entity.source === 'sed') sedIds.push(id);
    }
    if (sedIds.length > 0) store.removeEntities(sedIds);
    store.upsertEntities(entities);

    // Sample terrain heights so markers are placed at correct elevation
    const terrainHeights = await sampleTerrainHeights(entities);

    buildEntities(entities, state.swiss.wallDataSource, state.swiss.staticDataSource, MAX_QUAKES_SWISS, SWISS_SCALE, SWISS_RING_SPECS, true, terrainHeights);
    startAnimation();

    state.swiss.loaded = true;
    console.log(`[QuakeLayer] Loaded ${entities.length} Swiss earthquakes`);
  } catch (err) {
    console.error('[QuakeLayer] Failed to load Swiss data:', err);
  } finally {
    if (state) state.swiss.loading = false;
  }
}

/**
 * Sample terrain heights at earthquake positions using Cesium World Terrain.
 * Returns a Map<entityId, terrainHeightMeters>.
 */
async function sampleTerrainHeights(
  entities: Entity[],
): Promise<Map<string, number>> {
  const heights = new Map<string, number>();
  if (!state) return heights;

  const terrainProvider = state.viewer.terrainProvider;
  // If no terrain provider or it's the default ellipsoid, skip
  if (!terrainProvider || terrainProvider instanceof Cesium.EllipsoidTerrainProvider) {
    console.warn('[QuakeLayer] No terrain provider — cannot sample heights');
    return heights;
  }

  try {
    const positions = entities.map(e =>
      Cesium.Cartographic.fromDegrees(e.position.lon, e.position.lat),
    );

    const sampled = await Cesium.sampleTerrainMostDetailed(terrainProvider, positions);

    for (let i = 0; i < entities.length; i++) {
      const height = sampled[i].height;
      if (height !== undefined && !isNaN(height)) {
        heights.set(entities[i].id, height);
      }
    }
    console.log(`[QuakeLayer] Sampled terrain heights for ${heights.size}/${entities.length} positions`);
  } catch (err) {
    console.warn('[QuakeLayer] Terrain sampling failed (markers will use ellipsoid):', err);
  }

  return heights;
}

async function loadData(): Promise<void> {
  const globeState = useGlobeStore.getState();
  await Promise.all([
    globeState.quakeGlobalVisible ? loadGlobalData() : Promise.resolve(),
    globeState.quakeSwissVisible ? loadSwissData() : Promise.resolve(),
  ]);
}

// ─── Visibility helpers ──────────────────────────────────────────

function updateSubLayerVisibility(): void {
  if (!state) return;
  const gs = useGlobeStore.getState();
  const parentVisible = gs.quakeLayerVisible;

  state.global.wallDataSource.show = parentVisible && gs.quakeGlobalVisible;
  state.global.staticDataSource.show = parentVisible && gs.quakeGlobalVisible;
  state.swiss.wallDataSource.show = parentVisible && gs.quakeSwissVisible;
  state.swiss.staticDataSource.show = parentVisible && gs.quakeSwissVisible;
}

// ─── Public API ──────────────────────────────────────────────────

export function initQuakeLayer(viewer: Cesium.Viewer): void {
  if (state) return;

  // Global sub-layer data sources
  const globalWall = new Cesium.CustomDataSource('earthquakes-global-wall');
  const globalStatic = new Cesium.CustomDataSource('earthquakes-global-static');
  viewer.dataSources.add(globalWall);
  viewer.dataSources.add(globalStatic);

  // Swiss sub-layer data sources
  const swissWall = new Cesium.CustomDataSource('earthquakes-swiss-wall');
  const swissStatic = new Cesium.CustomDataSource('earthquakes-swiss-static');
  viewer.dataSources.add(swissWall);
  viewer.dataSources.add(swissStatic);

  state = {
    viewer,
    global: {
      wallDataSource: globalWall,
      staticDataSource: globalStatic,
      loaded: false,
      loading: false,
    },
    swiss: {
      wallDataSource: swissWall,
      staticDataSource: swissStatic,
      loaded: false,
      loading: false,
    },
    unsubscribeVisibility: null,
    refreshTimer: null,
    currentFeed: 'all_day',
  };

  // Apply initial visibility
  updateSubLayerVisibility();

  state.unsubscribeVisibility = useGlobeStore.subscribe((curr, prev) => {
    if (!state) return;

    const parentChanged = curr.quakeLayerVisible !== prev.quakeLayerVisible;
    const globalChanged = curr.quakeGlobalVisible !== prev.quakeGlobalVisible;
    const swissChanged = curr.quakeSwissVisible !== prev.quakeSwissVisible;

    if (!parentChanged && !globalChanged && !swissChanged) return;

    updateSubLayerVisibility();

    if (curr.quakeLayerVisible) {
      // Load data for newly enabled sub-layers
      if (parentChanged || (globalChanged && curr.quakeGlobalVisible)) {
        if (!state.global.loaded && curr.quakeGlobalVisible) loadGlobalData();
        else startAnimation();
      }
      if (parentChanged || (swissChanged && curr.quakeSwissVisible)) {
        if (!state.swiss.loaded && curr.quakeSwissVisible) loadSwissData();
        else startAnimation();
      }

      if (!state.refreshTimer) {
        state.refreshTimer = setInterval(() => loadData(), REFRESH_INTERVAL_MS);
      }
    } else {
      stopAnimation();
      if (state.refreshTimer) {
        clearInterval(state.refreshTimer);
        state.refreshTimer = null;
      }
    }

    viewer.scene.requestRender();
  });

  const visible = useGlobeStore.getState().quakeLayerVisible;
  if (visible) {
    loadData();
    state.refreshTimer = setInterval(() => loadData(), REFRESH_INTERVAL_MS);
  }

  console.log('[QuakeLayer] Initialized (global + swiss)');
}

export function destroyQuakeLayer(): void {
  if (!state) return;

  if (state.unsubscribeVisibility) state.unsubscribeVisibility();
  if (state.refreshTimer) clearInterval(state.refreshTimer);
  stopAnimation();

  state.viewer.dataSources.remove(state.global.wallDataSource, true);
  state.viewer.dataSources.remove(state.global.staticDataSource, true);
  state.viewer.dataSources.remove(state.swiss.wallDataSource, true);
  state.viewer.dataSources.remove(state.swiss.staticDataSource, true);
  useEntityStore.getState().clearByType('earthquake');

  state = null;
  console.log('[QuakeLayer] Destroyed');
}

export function getEarthquakeCount(): number {
  return selectEarthquakeCount(useEntityStore.getState());
}
